/**
 * Mailbox nudge store
 * -------------------
 * Backs the "add mailbox" nudges in the iFrame/standalone Control Panel.
 *
 * Single source of truth: `pendingAccounts` — rows as returned verbatim by
 * the suggestion API (`pending-accounts.service`), no transformation. Each
 * row is a (requester, suggestion) pair.
 *
 * "Added by you": the backend has no admin flag, so a row is the current
 * admin's own add when its `requesterEmail` matches the logged-in user's
 * email (admin self-adds carry that email — see the service). Detection is
 * derived in the getters via `isSelfAdded`, not stored on the row.
 *
 * NOTE: copy/i18n logic (the CTA matrix and banner copy matrix) lives
 * in `src/helpers/mailboxNudge.ts`, not in this store. Consumers call
 * those helpers with the raw state below; the store stays data-only.
 */

import type { ActionContext, Module } from "vuex";
import { getCurrentUserEmail } from "@/helpers/auth";

/**
 * Raw row as returned by the suggestion API (`r/email/suggestions/cp/get`,
 * under the `suggestions` array). Field names mirror the backend exactly so
 * no mapping is needed. Multiple rows can share the same `suggestionEmail`
 * when several people requested it; UI consumers should group by
 * `suggestionEmail` (see `pendingAccountGroups`).
 */
export type TPendingAccount = {
  bundleId?: number;
  requesterAid?: number;
  requesterName?: string;
  requesterEmail?: string;
  requestorClusterId?: number;
  suggestionEmail: string;
  status: "Requested" | "Created" | string;
};

/** UI-friendly grouped row: one entry per pending email + its requesters. */
export type TPendingAccountGroup = {
  suggestionEmail: string;
  /** All rows that requested this email, status="Requested" only. */
  requesters: Array<{
    aid?: number;
    name?: string;
    email?: string;
    isAdminAdded: boolean;
  }>;
  /** True when at least one of the requesters is the admin themselves. */
  hasAdminAdded: boolean;
};

const normalizeEmail = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/**
 * True when a row was added by the current admin themselves. Two signals:
 *  - No requester email at all → an add made directly from the CP (a team
 *    request always carries the requester's email), so it's the admin's own.
 *  - Otherwise the requester email matches the logged-in user (the value the
 *    CP add now sends — see pending-accounts.service).
 */
const isSelfAdded = (row: TPendingAccount): boolean => {
  const email = normalizeEmail(row.requesterEmail);
  if (!email) return true;
  return email === normalizeEmail(getCurrentUserEmail());
};

/**
 * Emails of mailboxes that already exist on the current domain, lowercased.
 * A pending row for one of these is stale — the mailbox got created (possibly
 * via another flow) but the backend row is still "Requested" — and must not
 * be surfaced as a suggestion. Empty when the mailbox list isn't loaded yet,
 * which makes the exclusion a no-op rather than hiding rows prematurely.
 */
const existingMailboxEmails = (rootGetters: {
  currentDomainMailboxDetails?: Array<{ email?: string }>;
}): Set<string> =>
  new Set(
    (rootGetters.currentDomainMailboxDetails || [])
      .map(a => normalizeEmail(a.email))
      .filter(Boolean)
  );

export type TMailboxNudgeState = {
  pendingAccounts: TPendingAccount[];
  /**
   * Session-scoped flag marking that cpWidget's first-mailbox lead-in
   * (licenses + no-mailboxes → redirect to Create) has been consumed for
   * this session (iFrame). Set either when cpWidget fires that
   * redirect on first load, or when the user clicks "Skip and go to
   * control panel" on the Create screen. cpWidget checks it so the
   * redirect happens once and never loops the user back into Create when
   * they navigate home afterwards. Resets on full reload.
   */
  skippedFirstMailbox: boolean;
};

const getDefaultState = (): TMailboxNudgeState => ({
  pendingAccounts: [],
  skippedFirstMailbox: false,
});

// Root state is untyped (rest of the store is JS). Use `any` for the
// root generic; getters reach into it via rootGetters with named keys.
type TCtx = ActionContext<TMailboxNudgeState, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

const mailboxNudgeModule: Module<TMailboxNudgeState, any> = {
  // eslint-disable-line @typescript-eslint/no-explicit-any
  namespaced: true,
  state: () => getDefaultState(),
  actions: {
    /** Replace the list — used by `pending-accounts.service.fetch*`. */
    pendingAccounts({ commit }: TCtx, payload: TPendingAccount[] | null) {
      commit("pendingAccounts", payload || []);
    },
    removePendingAccount({ commit, state }: TCtx, suggestionEmail: string) {
      // Case-insensitive + trimmed match — the create flow's email and the
      // queued row's email are built from domain sources that can differ in
      // case.
      const target = normalizeEmail(suggestionEmail);
      commit(
        "pendingAccounts",
        state.pendingAccounts.filter(a => normalizeEmail(a.suggestionEmail) !== target)
      );
    },
    addPendingAccount({ commit, state }: TCtx, row: TPendingAccount) {
      // Dedupe by (suggestionEmail + requesterEmail) so re-adding the same
      // entry doesn't create stacked rows for the admin self-add path.
      const exists = state.pendingAccounts.some(
        a =>
          normalizeEmail(a.suggestionEmail) === normalizeEmail(row.suggestionEmail) &&
          normalizeEmail(a.requesterEmail) === normalizeEmail(row.requesterEmail)
      );
      if (exists) return;
      commit("pendingAccounts", [...state.pendingAccounts, row]);
    },
    markFirstMailboxSkipped({ commit }: TCtx) {
      commit("skippedFirstMailbox", true);
    },
    resetState({ commit }: TCtx) {
      commit("resetState");
    },
  },
  mutations: {
    pendingAccounts(state, payload: TPendingAccount[]) {
      state.pendingAccounts = payload;
    },
    skippedFirstMailbox(state, payload: boolean) {
      state.skippedFirstMailbox = payload;
    },
    resetState(state) {
      Object.assign(state, getDefaultState());
    },
  },
  getters: {
    pendingAccounts: (state): TPendingAccount[] => state.pendingAccounts,
    skippedFirstMailbox: (state): boolean => state.skippedFirstMailbox,

    /**
     * Usernames the admin themselves queued — the admin-added rows on
     * `pendingAccounts`. Drives the pending-accounts banner's "you" copy and the
     * cpWidget/EmailAccounts banner count. Deduped by username.
     */
    adminPendingUsernames: (state, _getters, _rootState, rootGetters): string[] => {
      const existing = existingMailboxEmails(rootGetters);
      const out: string[] = [];
      const seen = new Set<string>();
      state.pendingAccounts.forEach(a => {
        if (a.status !== "Requested") return;
        if (existing.has(normalizeEmail(a.suggestionEmail))) return;
        if (!isSelfAdded(a)) return;
        // Banner copy renders these as `${username}@${domain}` — skip rows
        // whose suggestionEmail isn't email-shaped rather than render junk.
        const email = String(a.suggestionEmail || "");
        if (!email.includes("@")) return;
        const username = email.split("@")[0];
        if (!username || seen.has(username)) return;
        seen.add(username);
        out.push(username);
      });
      return out;
    },

    /** Convenience count for the pending-accounts banner copy. */
    adminPendingCount: (state, getters): number => getters.adminPendingUsernames.length,

    /**
     * Count of team-requested pending accounts — rows that are still
     * "Requested" AND were submitted by someone other than the admin.
     * Note: multiple team rows for the same `suggestionEmail` still count
     * as one team-request item (matches the banner copy intent).
     */
    teamRequestCount: (state, getters): number => getters.teamPendingEmails.length,

    /**
     * Team-requested pending emails (full addresses), deduped by
     * `suggestionEmail` — rows still "Requested" AND submitted by someone
     * other than the admin. Drives the team-only pending-banner copy, which
     * leads with the first entry (see `getPendingBannerCopy`).
     */
    teamPendingEmails: (state, _getters, _rootState, rootGetters): string[] => {
      const existing = existingMailboxEmails(rootGetters);
      const out: string[] = [];
      const seen = new Set<string>();
      state.pendingAccounts.forEach(a => {
        if (a.status !== "Requested") return;
        if (existing.has(normalizeEmail(a.suggestionEmail))) return;
        if (isSelfAdded(a)) return;
        const email = String(a.suggestionEmail || "");
        if (seen.has(email)) return;
        seen.add(email);
        out.push(email);
      });
      return out;
    },

    /**
     * Grouped UI shape: one entry per pending `suggestionEmail`, with the
     * full list of requesters. Filters out "Created" rows and rows whose
     * mailbox already exists on the domain (those aren't pending anymore).
     */
    pendingAccountGroups: (state, _getters, _rootState, rootGetters): TPendingAccountGroup[] => {
      const existingMailboxes = existingMailboxEmails(rootGetters);
      const byEmail = new Map<string, TPendingAccountGroup>();
      state.pendingAccounts.forEach(row => {
        if (row.status !== "Requested") return;
        if (existingMailboxes.has(normalizeEmail(row.suggestionEmail))) return;
        const existing = byEmail.get(row.suggestionEmail);
        const isAdminAdded = isSelfAdded(row);
        const requester = {
          aid: row.requesterAid,
          name: row.requesterName,
          email: row.requesterEmail,
          isAdminAdded,
        };
        if (existing) {
          existing.requesters.push(requester);
          if (isAdminAdded) existing.hasAdminAdded = true;
        } else {
          byEmail.set(row.suggestionEmail, {
            suggestionEmail: row.suggestionEmail,
            requesters: [requester],
            hasAdminAdded: isAdminAdded,
          });
        }
      });
      return Array.from(byEmail.values());
    },
  },
};

export default mailboxNudgeModule;
